import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogProps,
  Grid,
  useEventCallback,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { PmiButton } from 'shell/components/shared/button';
import { memo } from 'react';
import { FormikErrors, FormikTouched, useFormik } from 'formik';
import { PmiDialogTitle } from 'shell/components/shared/dialog/title';
import styled from '@emotion/styled';
import { BewareFormVM } from 'mutation/view-models/registration-beware';
import { validationSchema } from '../constants';
import { __PERSON_API_URL__ } from 'shell/constants/environment';
import { useHttpClient } from 'shell/hooks/use-http-fetch/stateless';
import { toast } from 'react-toastify';
import { ResponsePromise } from 'shell/hooks/use-http-fetch/stateless/type';
import FormBeware from '../../form-beware';

const PmiDialog = styled((props: DialogProps) => <Dialog {...props} />)((props) => ({
  '& .MuiDialog-paper': props.fullScreen
    ? {
        minWidth: 'unset',
        maxWidth: '100%',
      }
    : {
        minWidth: '50rem',
        maxWidth: '800px',
      },
}));

interface ModalEditBewareProps {
  initialValues?: BewareFormVM;
  open: boolean;
  onClose: (isEdit?: boolean) => void;
}

const ModalEditBewareComponent = ({
  initialValues,
  open,
  onClose,
}: ModalEditBewareProps): React.ReactElement => {
  const updateData = useHttpClient(`${__PERSON_API_URL__}/persona/diffide/aggiorna`, {
    options: {
      method: 'PUT',
    },
  });

  const onSubmit = useEventCallback((values: BewareFormVM) => {
    (
      updateData({
        data: {
          diffidaNew: {
            dalGiorno: values.dateForm ? values.dateForm.toFormat('yyyy-MM-dd') : undefined,
            perAnni: values.year ? Number.parseInt(values.year, 10) : undefined,
            note: values.note || undefined,
            comune: {
              denominazione: values.municipalityItalian?.description || undefined,
              codiceSiglaProvincia: values.municipalityItalian?.province || undefined,
            },
          },
          diffidaKey: { ...values.key },
        },
      }) as ResponsePromise<void>
    ).then(() => {
      toast.success('Operazione eseguita con successo');

      onClose(true);
    });
  });

  const formik = useFormik<BewareFormVM>({
    initialValues: initialValues || {
      typology: '',
      municipalityItalian: null,
      dateForm: null,
      year: undefined,
      note: '',
    },
    enableReinitialize: true,
    validationSchema,
    onSubmit,
  });

  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down(theme.breakpoints.values.md));

  return (
    <PmiDialog
      open={open}
      onClose={() => onClose(false)}
      aria-labelledby="alert-dialog-title"
      aria-describedby="alert-dialog-description"
      fullScreen={fullScreen}
    >
      <PmiDialogTitle id="alert-dialog-title" backgroundColor="primary" color="white">
        Modifica Diffida
      </PmiDialogTitle>
      <DialogContent style={{ padding: '30px 24px' }}>
        <Grid container spacing={2}>
          <FormBeware
            values={formik.values}
            onChange={formik.setFieldValue}
            touched={formik.touched as FormikTouched<BewareFormVM>}
            errors={formik.errors as FormikErrors<BewareFormVM>}
            isEdit
          />
        </Grid>
      </DialogContent>
      <DialogActions>
        <PmiButton
          variant="contained"
          color="primary"
          type="submit"
          onClick={() => formik.handleSubmit()}
        >
          Conferma
        </PmiButton>
        <PmiButton variant="outlined" color="cdmBrand" onClick={() => onClose(false)}>
          Annulla
        </PmiButton>
      </DialogActions>
    </PmiDialog>
  );
};

ModalEditBewareComponent.displayName = 'Modal Annotation form component';

const ModalEditBeware = memo(ModalEditBewareComponent);

export default ModalEditBeware;
